import { RoomConfiguration as GraphQLRoomConfiguration } from 'generated/graphql';
import { RoomConfiguration as ReduxRoomConfiguration } from 'store/groupDetailsSlice';

/**
 * Core room configuration interface that both GraphQL and Redux types implement
 * This represents the common shape that all room configurations share
 */
export interface RoomConfigurationCore {
  rooms: number;
  guests: number;
}

/**
 * Union type to handle both GraphQL and Redux types
 * Both types structurally conform to RoomConfigurationCore
 */
export type CompatibleRoomConfiguration =
  | GraphQLRoomConfiguration
  | ReduxRoomConfiguration;

/**
 * Utility type to extract only the core fields from any room configuration type
 * This is useful when you need to work with just the essential data
 *
 * Example usage:
 * const coreData: RoomConfigurationCore = extractCoreFields(someConfig);
 */
export type ExtractCoreFields<T extends RoomConfigurationCore> = Pick<
  T,
  keyof RoomConfigurationCore
>;

/**
 * Helper function to extract core fields from any compatible room configuration
 * This is useful when interfacing with APIs that expect only core fields
 *
 * @param config Any compatible room configuration
 * @returns Object with only rooms and guests properties
 */
export const extractCoreFields = <T extends CompatibleRoomConfiguration>(
  config: T,
): RoomConfigurationCore => ({
  rooms: config.rooms,
  guests: config.guests,
});

/**
 * Type guard to ensure we're working with valid room configuration data
 * This provides runtime type safety without normalization overhead
 */
export const isValidRoomConfiguration = (
  config: unknown,
): config is RoomConfigurationCore => {
  if (typeof config !== 'object' || config === null) {
    return false;
  }

  const configObj = config as Record<string, unknown>;

  return (
    'rooms' in configObj &&
    'guests' in configObj &&
    typeof configObj.rooms === 'number' &&
    typeof configObj.guests === 'number'
  );
};

/**
 * Formats room configurations into a displayable format
 * @param configurations Array of room configurations to format (GraphQL or Redux)
 * @returns Objects with formatted text and room data for display
 */
export const formatRoomConfigurations = (
  configurations: CompatibleRoomConfiguration[],
) => {
  if (!configurations || configurations.length === 0) {
    return [];
  }

  return configurations.map((config, index) => {
    // Direct access to properties - no normalization needed
    // Both GraphQL and Redux types have the same structure for rooms/guests
    const guestText = config.guests === 1 ? 'guest' : 'guests';
    const roomText = config.rooms === 1 ? 'room' : 'rooms';

    return {
      key: index,
      text: `${config.rooms} ${roomText} (${config.guests} ${guestText} each)`,
      rooms: config.rooms,
      guests: config.guests,
    };
  });
};

/**
 * Calculates the total number of rooms from room configurations
 * @param configurations Array of room configurations (GraphQL or Redux)
 * @returns Total number of rooms
 */
export const calculateTotalRooms = (
  configurations: CompatibleRoomConfiguration[],
): number => {
  if (!configurations || configurations.length === 0) {
    return 0;
  }

  return configurations.reduce((sum, config) => sum + config.rooms, 0);
};

/**
 * Validates a single room configuration
 * @param config Room configuration to validate (GraphQL or Redux)
 * @returns True if configuration is valid
 */
export const validateRoomConfiguration = (
  config: CompatibleRoomConfiguration,
): boolean => {
  return (
    Number.isInteger(config.rooms) &&
    config.rooms >= 0 &&
    Number.isInteger(config.guests) &&
    config.guests >= 1 &&
    config.guests <= 2
  );
};

/**
 * Validates multiple room configurations
 * @param configurations Array of room configurations to validate (GraphQL or Redux)
 * @returns True if all configurations are valid
 */
export const validateRoomConfigurations = (
  configurations: CompatibleRoomConfiguration[],
): boolean => {
  if (!configurations || configurations.length === 0) {
    return true;
  }

  return configurations.every(validateRoomConfiguration);
};
